import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/components/Sidebar.css';
import { OvertureLogo, WinterSantaHatLogo } from '../assets/images/brand';
import { useAuth } from '../../domains/auth/contexts/AuthContext';
import { Muse } from '../../types';
import UserProfileSidebar from './UserProfileSidebar';
import { useTheme } from '../contexts/ThemeContext';
import { hasAnyAdminPermission } from '../../domains/auth/adminPermissions';
import { hasFullPlatformStaffStatus } from '../../domains/auth/teacherAccess';

interface SidebarProps {
  selectedMuse?: Muse | null;
  museCash?: number;
  stars?: number;
  lastReward?: number;
  lastStarReward?: number;
  onLastRewardComplete?: () => void;
  onLastStarRewardComplete?: () => void;
  onLogout?: () => Promise<void>;
  isMobile?: boolean;
  autumnMode?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  selectedMuse = null,
  museCash = 0,
  stars = 0,
  lastReward = 0,
  lastStarReward = 0,
  onLastRewardComplete = () => {},
  onLastStarRewardComplete = () => {},
  onLogout = async () => {},
  isMobile = false,
  autumnMode
}) => {
  const { userData } = useAuth();
  const { autumnMode: themeAutumnMode } = useTheme();
  const activeAutumn = (autumnMode !== undefined ? autumnMode : themeAutumnMode);
  const logoSrc = activeAutumn ? WinterSantaHatLogo : OvertureLogo;
  const isTeacher = userData?.role === 'teacher';
  const isParent = userData?.role === 'parent';
  const isAdmin = userData?.isAdmin === true;
  const hasAdminTools = hasAnyAdminPermission(userData);
  const staffStatus = (isTeacher ? (userData?.staffStatus ?? 'active') : null);
  const isRestrictedTeacher = isTeacher && !isAdmin && !hasFullPlatformStaffStatus(staffStatus);

  return (
    <aside
      className={`sidebar ${isParent ? 'sidebar--parent' : ''}`}
      style={{
        background: activeAutumn
          ? 'linear-gradient(135deg, #0b1a3a, #102a5c)'
          : 'linear-gradient(135deg, #4FC3F7, #0288D1)',
        fontFamily: 'Fredoka One, Arial Rounded MT Bold, Arial, sans-serif'
      }}
    >
      <div
        className="sidebar-logo"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '1.2rem 0 0.5rem 0',
          userSelect: 'none'
        }}
        title="Overture"
      >
        <img
          src={logoSrc}
          alt="Overture Logo"
          style={{
            height: 50,
            borderRadius: '1rem',
            background: 'rgba(255,255,255,0.92)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
            padding: '0.2rem 1rem',
            marginBottom: 8,
            transition: 'transform 0.18s ease, box-shadow 0.18s ease'
          }}
          className="sidebar-logo-img"
        />
      </div>

      {/* User Profile Section */}
      <UserProfileSidebar
        userData={userData}
        selectedMuse={selectedMuse}
        museCash={museCash}
        stars={stars}
        lastReward={lastReward}
        lastStarReward={lastStarReward}
        onLastRewardComplete={onLastRewardComplete}
        onLastStarRewardComplete={onLastStarRewardComplete}
        onLogout={onLogout}
        isCompact={isMobile}
      />

      {/* Navigation Links - Single Section */}
      <div className="sidebar-navigation">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
          {isTeacher && isRestrictedTeacher && (
            <Link to="/app/home" className="sidebar-link">
              <span className="sidebar-icon">🏠</span>Home
            </Link>
          )}
          {isTeacher && !isRestrictedTeacher && (
            <Link to="/app/squads" state={{ squadsTab: 'dashboard' }} className="sidebar-link">
              <span className="sidebar-icon">🏠</span>Home
            </Link>
          )}
          {isTeacher && (
            <Link to="/app/onboarding" className="sidebar-link">
              <span className="sidebar-icon">✅</span>Certifications
            </Link>
          )}
          {isTeacher && isRestrictedTeacher && (
            <Link to="/app/profile" className="sidebar-link">
              <span className="sidebar-icon">👤</span>Profile
            </Link>
          )}
          {isParent && (
            <Link to="/app/students" className="sidebar-link">
              <span className="sidebar-icon">👨‍👩‍👧‍👦</span>Students
            </Link>
          )}
          {!isParent && !isRestrictedTeacher && (
            <>
              <Link to="/app/games" state={{ refresh: Date.now() }} className="sidebar-link">
                <span className="sidebar-icon">🎮</span>Games
              </Link>
              <Link to="/app/library" className="sidebar-link">
                <span className="sidebar-icon">📖</span>Universe
              </Link>
              <Link to="/app/songs" className="sidebar-link">
                <span className="sidebar-icon">🎵</span>Creations
              </Link>
              <Link to="/app/market" className="sidebar-link">
                <span className="sidebar-icon">🏛️</span>Muse Vault
              </Link>
              <Link to="/app/mymuses" className="sidebar-link">
                <span className="sidebar-icon">🎭</span>My Muses
              </Link>
            </>
          )}
          {hasAdminTools && !isRestrictedTeacher && (
            <Link to="/app/admin" className="sidebar-link">
              <span className="sidebar-icon">🛠️</span>{isAdmin ? 'Admin' : 'Admin Tools'}
            </Link>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
